import { useCallback, useMemo } from 'react';

export type Tier = { points: number; label: string };

type UseProgressCalculationProps = {
  currentPoints: number;
  lastSeenLoyaltyPoints: number;
  tiers: Tier[];
};

export type ProgressData = {
  progressPercentage: number;
  pointsNeededForNextTier: number;
  currentTier: number;
};

export type AnimationState = {
  shouldAnimate: boolean;
  startValue: number;
  targetValue: number;
};

export const useProgressCalculation = ({
  currentPoints,
  lastSeenLoyaltyPoints,
  tiers = [],
}: UseProgressCalculationProps) => {
  const getNextTierInfo = useCallback(
    (pointsValue: number) => {
      const nextTierIndex = tiers.findIndex(cat => cat.points > pointsValue);
      const nextTierPoints = nextTierIndex !== -1 ? tiers[nextTierIndex].points : 0;
      return { nextTierIndex, nextTierPoints };
    },
    [tiers]
  );

  const calculateProgressPercentage = useCallback(
    (pointsValue: number) => {
      const categoriesLength = tiers.length;
      if (categoriesLength <= 1) {
        return 0;
      }
      const nextTierIndex = tiers.findIndex(cat => cat.points > pointsValue);
      if (nextTierIndex === -1) {
        return 100;
      }

      // If the user has not reached the first tier, calculate the progress of the first part of the bar.
      // (it has a different scale from the rest of the bar)
      if (nextTierIndex === 0) {
        return (pointsValue / tiers[0].points) * 100;
      }

      const currentTierIndex = Math.max(0, nextTierIndex - 1);
      const currentTierPoints = tiers[currentTierIndex].points;
      const nextTierPoints = tiers[nextTierIndex].points;

      const segmentWidth = 100 / (categoriesLength - 1);
      const denom = nextTierPoints - currentTierPoints;
      const within = denom > 0 ? (pointsValue - currentTierPoints) / denom : 0;

      const progress = (currentTierIndex + within) * segmentWidth;

      return Math.min(Math.max(progress, 0), 100);
    },
    [tiers]
  );

  const progressData: ProgressData = useMemo(() => {
    const { nextTierPoints, nextTierIndex } = getNextTierInfo(currentPoints);
    const pointsNeededForNextTier = nextTierPoints - currentPoints;
    const progressPercentage = calculateProgressPercentage(currentPoints);
    return {
      progressPercentage,
      pointsNeededForNextTier,
      currentTier: nextTierIndex === -1 ? tiers.length : nextTierIndex,
    };
  }, [getNextTierInfo, currentPoints, calculateProgressPercentage, tiers.length]);

  const animationState: AnimationState = useMemo(() => {
    if (!progressData || !tiers.length) {
      return { shouldAnimate: false, startValue: 0, targetValue: 0 };
    }

    const lastSeenBeforeFirstTier = lastSeenLoyaltyPoints < tiers[0].points;
    const isAfterFirstTier = currentPoints >= tiers[0].points;
    const startAnimationAtFirstTier = lastSeenBeforeFirstTier && isAfterFirstTier;

    const shouldAnimate = currentPoints > lastSeenLoyaltyPoints;
    const startValue = calculateProgressPercentage(
      startAnimationAtFirstTier ? tiers[0].points : lastSeenLoyaltyPoints
    );
    const targetValue = progressData.progressPercentage;
    return { shouldAnimate, startValue, targetValue };
  }, [progressData, lastSeenLoyaltyPoints, tiers, currentPoints, calculateProgressPercentage]);

  return {
    progressData,
    animationState,
    calculateProgressPercentage,
    getNextTierInfo,
  };
};
